import { GenService, GenServiceMethods } from "@bufbuild/protobuf/codegenv2";
import { createClient, Interceptor, Transport } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { useAuth } from "@workos-inc/authkit-react";

import { API_BASE_URL, IS_DEBUG, IS_PRODUCTION } from "@/constants";

/**
 * Create an interceptor for adding the Authorization header to the request
 */
export function createAuthInterceptor(
  getAccessToken: ReturnType<typeof useAuth>["getAccessToken"],
): Interceptor {
  return (next) => async (req) => {
    try {
      const token = await getAccessToken();
      if (token) {
        req.header.set("Authorization", `Bearer ${token}`);
        if (IS_DEBUG) {
          console.debug("[Auth] Token set, length:", token.length);
        }
      } else {
        if (IS_DEBUG) {
          console.warn("[Auth] No token available from getAccessToken()");
        }
      }
    } catch (error) {
      if (IS_DEBUG) {
        console.warn("[Auth] Failed to get access token:", error);
      }
    }
    return next(req);
  };
}

/**
 * Create an interceptor for request/response logging in development
 */
export function createLoggingInterceptor(): Interceptor {
  return (next) => async (req) => {
    if (IS_DEBUG) {
      console.debug("[Connect-RPC] Request:", {
        service: req.service.typeName,
        method: req.method.name,
        url: req.url,
        headers: Object.fromEntries(req.header),
      });
    }

    try {
      const res = await next(req);

      if (IS_DEBUG) {
        console.debug("[Connect-RPC] Response:", {
          service: req.service.typeName,
          method: req.method.name,
          status: "success",
        });
      }

      return res;
    } catch (error) {
      if (IS_DEBUG) {
        console.debug("[Connect-RPC] Error:", {
          service: req.service.typeName,
          method: req.method.name,
          error,
        });
      }
      throw error;
    }
  };
}

export const createEdgeTransport = ({
  baseUrl,
  interceptors,
  useBinaryFormat = IS_PRODUCTION,
}: {
  baseUrl: string;
  interceptors: Interceptor[];
  useBinaryFormat?: boolean;
}): Transport => {
  return createConnectTransport({
    baseUrl,
    useBinaryFormat,
    interceptors,
  });
};

export const createEdgeClient = <T extends GenService<GenServiceMethods>>({
  service,
  getAccessToken,
}: {
  service: T;
  getAccessToken: ReturnType<typeof useAuth>["getAccessToken"];
}) => {
  const interceptors = [createAuthInterceptor(getAccessToken), createLoggingInterceptor()];

  const transport = createEdgeTransport({
    baseUrl: API_BASE_URL,
    interceptors,
    useBinaryFormat: IS_PRODUCTION,
  });

  return createClient(service, transport);
};
